import React from 'react';

import { getPreziBrief, getPreziBriefVariables } from '__generated__/getPreziBrief';
import { PreziBriefFields } from '__generated__/PreziBriefFields';
import Brief from 'projects/routes/Brief';
import BriefQuery from 'projects/routes/BriefQuery';
import InviteDesignersPage from 'projects/routes/InviteDesignersPage';
import { Industries } from 'projects/util/industries';
import { BriefStep, StepState } from 'projects/util/step';
import { RouteComponentProps, withRouter } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import AboutYouPage from './AboutYouPage';
import DetailsPage from './DetailsPage';
import queries from './PreziBrief.graphql';
import SummaryPage from './SummaryPage';
import TimeAndBudgetPage from './TimeAndBudgetPage';

type Props = RouteComponentProps<{
    briefId: string;
    step: string;
}>;

const steps: BriefStep<PreziBriefFields>[] = [
    {
        step: 1,
        title: __('Tell us about your Prezi project'),
        fields: [
            'language',
            'title',
            'description',
            'projectType',
            'existingPreziUrls',
            'preziClassicUrls',
            'powerpointFiles',
            'referenceFiles',
            'inspirationFiles',
            'inspirationUrls',
        ],
    },
    { step: 2, title: __('Your timeline') },
    { step: 3, title: __('About you') },
    { step: 4, title: __('Review your brief') },
    {
        step: 5,
        title: __('Invite designers to quote'),
        isAuthRequired: true,
        isDesignerStep: true,
        nextActionLabel: __('Confirm designers'),
    },
];

export const PreziBrief: React.FunctionComponent<Props> = props => (
    <BriefQuery<getPreziBrief, getPreziBriefVariables>
        query={queries.getPreziBrief}
        variables={{ id: props.match.params.briefId }}
        fieldsTypename="PreziBriefFields"
    >
        {(data, refetch) => {
            const step = new StepState({
                steps,
                brief: data.brief,
                currentStep: parseInt(props.match.params.step, 10),
            });

            const brief = {
                ...data.brief,
                fields: data.brief.fields as PreziBriefFields,
            };

            const industries = new Industries(data.industries);

            return (
                <Brief
                    refetch={refetch}
                    schema="prezi"
                    brief={brief}
                    viewer={data.viewer}
                    stepState={step}
                >
                    {form => (
                        <Switch>
                            <Route
                                path={`/briefs/prezi/:briefId/1`}
                                render={() => <DetailsPage form={form} brief={brief} />}
                            />
                            <Route
                                path={`/briefs/prezi/:briefId/2`}
                                render={() => <TimeAndBudgetPage form={form} brief={brief} />}
                            />
                            <Route
                                path={`/briefs/prezi/:briefId/3`}
                                render={() => (
                                    <AboutYouPage
                                        form={form}
                                        brief={brief}
                                        industries={data.industries}
                                    />
                                )}
                            />
                            <Route
                                path={`/briefs/prezi/:briefId/4`}
                                render={() => <SummaryPage brief={brief} industries={industries} />}
                            />
                            <Route
                                path={`/briefs/prezi/:briefId/5`}
                                render={() => (
                                    <InviteDesignersPage
                                        brief={brief}
                                        viewer={data.viewer}
                                        description={__(
                                            'Here are the Prezi Certified Experts that best match your needs'
                                        )}
                                    />
                                )}
                            />
                        </Switch>
                    )}
                </Brief>
            );
        }}
    </BriefQuery>
);

export default withRouter(PreziBrief);
